import React, { useState, useEffect } from 'react';
import { useForm, yupResolver } from '@mantine/form';
import * as Yup from 'yup';
import {
    Button,
    TextInput, Modal, Text, Center,
    FileInput,
    Progress,
    Group,
    SimpleGrid,
    Box,
    NumberInput,
    Paper
} from '@mantine/core';
import { updateS3UrlMasterClass, updateMasterClass, getMasterClassDetails } from '../../../api/masterClass';
import axios from 'axios';
import { useParams, useNavigate } from "react-router-dom";
import { useDisclosure } from '@mantine/hooks';

const languages = [
    { value: 'en', label: 'English' },
    { value: 'fr', label: 'French' },
    { value: 'es', label: 'Spanish' },
    { value: 'it', label: 'Italian' },
    { value: 'de', label: 'German' },
    { value: 'pt', label: 'Portuguese' },
    { value: 'nl', label: 'Dutch' },
    { value: 'ar', label: 'Arabic' },
];

const VALID_VIDEO_TYPES = ['video/mp4', 'video/avi', 'video/mov'];


const validationSchema = Yup.object(
    languages.reduce(
        (schema, lang) => {
            const nameField = `name_${lang.value}`;
            const fileField = `file_${lang.value}`;

            if (lang.value === 'en') {
                // English: Name is required, and video file is required and must be valid.
                schema[nameField] = Yup.string().required('English name is required');
                schema[fileField] = Yup.mixed()
                    .nullable()

                    .test('fileIsString', 'File must be a valid string or video file', (value) => {
                        return typeof value === 'string' || (value && VALID_VIDEO_TYPES.includes(value.type));
                    })
                    .required('Video file is required for English');
            } else {
                // Other languages: Name is optional, but video is required if name is provided.
                schema[nameField] = Yup.string();
                schema[fileField] = Yup.mixed()
                    .nullable()

                    .test('fileIsString', 'File must be a valid string or video file', (value) => {
                        return typeof value === 'string' || value === null || (value && VALID_VIDEO_TYPES.includes(value.type));
                    })
                    .test(
                        'fileRequiredIfNameProvided',
                        `${lang.label} video is required when ${lang.label} name is provided`,
                        function (value) {
                            const nameValue = this.parent[nameField];
                            return !nameValue || value;
                        }
                    );
            }

            return schema;
        },
        {
            // Priority is required and must be a number >= 1.
            priority: Yup.number()
                .typeError('Priority must be a number')
                .required('Priority is required')
                .min(1, 'Must be at least 1'),
        }
    )
);


const VideoUploadForm = () => {

    const [uploadProgress, setUploadProgress] = useState({});
    const [videoPreviews, setVideoPreviews] = useState({});
    const [message, setMessage] = useState('');
    const [uploading, setUploading] = useState(false);
    const params = useParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [opened, { open, close }] = useDisclosure(false);

    const form = useForm({
        initialValues: languages.reduce((values, lang) => {
            values[`name_${lang.value}`] = '';
            values[`file_${lang.value}`] = null;
            return values;
        }, { priority: '' }),
        validate: yupResolver(validationSchema),
    });

    const handleFileChange = (file, lang) => {
        if (file) {
            form.setFieldValue(`file_${lang}`, file);
            setVideoPreviews((prev) => ({ ...prev, [lang]: URL.createObjectURL(file) }));
        } else {
            form.setFieldValue(`file_${lang}`, null);
            setVideoPreviews((prev) => ({ ...prev, [lang]: null }));
        }
    };

    useEffect(() => {
        const fetchMasterClassDetails = async () => {
            try {
                const data = await getMasterClassDetails(params?.id);
                const formData = { priority: data.priority };

                languages.forEach((lang) => {
                    formData[`name_${lang.value}`] = data?.name_local?.[lang.value] || '';
                    // formData[`file_${lang.value}`] = null;

                    if (data?.link_local?.[lang.value]) {
                        formData[`file_${lang.value}`] = data?.link_local?.[lang.value] ?? null;
                        setVideoPreviews((prev) => ({
                            ...prev,
                            [lang.value]: data?.link_local?.[lang.value],
                        }));
                    }
                });

                form.setValues(formData);
                setIsLoading(false)
            } catch (error) {
                console.error('Error fetching master class details:', error);
            } finally {
                setIsLoading(false)
            }
        };

        fetchMasterClassDetails();
    }, [params?.moduleId]);

    useEffect(() => {
        return () => {
            Object.values(videoPreviews).forEach((preview) => {
                if (preview) URL.revokeObjectURL(preview);
            });
        };
    }, [videoPreviews]);

    const handleSubmit = async (values) => {
        setMessage('');
        setUploadProgress({});
        setUploading(true);

        try {
            // Filter out languages that do not have both name and file (except English which is always required)
            const languagesToProcess = languages.filter((lang) => {
                if (lang.value === 'en') {
                    return true; // English is always required
                }
                const file = values[`file_${lang.value}`];
                const name = values[`name_${lang.value}`]?.trim();
                // Include this language only if name and file are provided
                return name && file;
            });

            const requests = await Promise.all(
                languagesToProcess.map(async (lang) => {
                    const file = values[`file_${lang.value}`];
                    const name = values[`name_${lang.value}`];

                    const formData = new FormData();
                    formData.append('module_id', params?.moduleId);
                    formData.append('id', params?.id);
                    formData.append('language', lang.value);
                    formData.append('name', name);
                    formData.append('filename', file.name);
                    formData.append('filetype', file.type);

                    if (file.name) {
                        const { uploadUrl, fileKey } = await updateS3UrlMasterClass(formData);

                        await axios.put(uploadUrl, file, {
                            headers: { 'Content-Type': file.type },
                            onUploadProgress: (progressEvent) => {
                                const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                                setUploadProgress((prev) => ({ ...prev, [lang.value]: progress }));
                            },
                        });

                        return {
                            [`link_${lang.value}`]: fileKey,
                            language: lang.value,
                            [`name_${lang.value}`]: name,
                            filename: file.name,
                            filetype: file.type,
                        };
                    } else {
                        return {
                            // [`link_${lang.value}`]: file,
                            // language: lang.value,
                            [`name_${lang.value}`]: name,
                            // filename: file.name,
                            // filetype: file.type,
                        };
                    }




                })
            );

            await updateMasterClass({
                module_id: params?.moduleId,
                priority: values.priority,
                id: params?.id,
                ...Object.assign({}, ...requests),
            });

            setMessage('All files uploaded successfully');
            form.reset();
            setVideoPreviews({});
            navigate(-1, { replace: true });
        } catch (error) {
            console.error(error);
            setMessage('Error uploading files');
        } finally {
            setUploading(false);
        }
    };

    if (isLoading) {
        return <div>Loading...</div>; // or a spinner
    }

    return (
        <form onSubmit={form.onSubmit(handleSubmit)}>
            <NumberInput
                label="Priority"
                placeholder="Enter priority"
                {...form.getInputProps('priority')}
                error={form.errors.priority}
                mb={"md"}
            />

            <SimpleGrid cols={2} spacing="md">
                {languages.map((lang) => (
                    <Paper shadow="md" p="xl" withBorder key={lang.value}>
                        <Text my={"md"} fw={700}>{lang.label}</Text>
                        <TextInput
                            label={`Name`}
                            labelProps={{
                                style: { color: "#9da1a5" }
                            }}
                            mb={"md"}
                            placeholder={`Enter name for ${lang.label}`}
                            {...form.getInputProps(`name_${lang.value}`)}
                            error={form.errors[`name_${lang.value}`]}
                        />

                        <FileInput
                            labelProps={{
                                style: { color: "#9da1a5" }
                            }}
                            label={`Video File`}
                            placeholder={`Select a video file for ${lang.label}`}
                            accept="video/*"
                            onChange={(file) => handleFileChange(file, lang.value)}
                            error={form.errors[`file_${lang.value}`]}
                        />

                        {videoPreviews[lang.value] && (
                            <Box mt="md" key={videoPreviews[lang.value]}>
                                <Text size="sm" weight={500}>
                                    Video Preview ({lang.label}):
                                </Text>
                                <video controls width="100%" style={{ height: 300 }}>
                                    <source src={videoPreviews[lang.value]} type={form.values[`file_${lang.value}`]?.type || 'video/mp4'} />
                                    Your browser does not support the video tag.
                                </video>
                            </Box>
                        )}

                        {uploadProgress[lang.value] > 0 && (
                            <Progress
                                value={uploadProgress[lang.value]}
                                label={`${uploadProgress[lang.value]}%`}
                                size="lg"
                                mt="md"
                            />
                        )}
                    </Paper>
                ))}
            </SimpleGrid>

            {message && <Text mt="md">{message}</Text>}

            <Group position="right" mt="md">
                <Button type="submit" disabled={uploading}>
                    {uploading ? 'Uploading...' : 'Submit'}
                </Button>
                &nbsp;
                <Button type="button" mt="md" color="red" onClick={open} >
                    Return
                </Button>

                <Modal opened={opened} onClose={close}
                    centered
                    title="Confirm">

                    <Text>Are you sure you want to leave this page without saving?</Text>


                    <Center mt={"md"}>
                        <Button onClick={() => window.history.back()}
                        >Confirm</Button>
                    </Center>

                </Modal >
            </Group>
        </form>
    );
};

export default VideoUploadForm;
